import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, BookOpen, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  Zap, Wind, Info
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const CATEGORIES = [
  { id: 'all',          label: 'All Materials' },
  { id: 'wood',         label: 'Wood' },
  { id: 'acrylic',      label: 'Acrylic' },
  { id: 'leather',      label: 'Leather' },
  { id: 'fabric',       label: 'Fabric' },
  { id: 'paper',        label: 'Paper' },
  { id: 'stone',        label: 'Stone' },
  { id: 'metal',        label: 'Metal' },
  { id: 'glass',        label: 'Glass' },
  { id: 'rubber',       label: 'Rubber' },
  { id: 'never_laser',  label: '⛔ Never Laser' },
];

const LASER_TYPES = [
  { id: 'all',    label: 'All Types' },
  { id: 'diode',  label: 'Diode' },
  { id: 'co2',    label: 'CO2' },
  { id: 'fibre',  label: 'Fibre' },
];

const TYPE_BADGE = {
  co2:   'bg-blue-700',
  diode: 'bg-purple-700',
  fibre: 'bg-yellow-700',
  galvo: 'bg-green-700',
};

const MaterialsGuide = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [category, setCategory]   = useState('all');
  const [laserType, setLaserType] = useState('all');
  const [expanded, setExpanded]   = useState({});

  useEffect(() => {
    axios.get(`${API}/laser/materials-guide`)
      .then(res => setMaterials(res.data || []))
      .catch(err => console.error('Failed to load materials guide:', err))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const filtered = useMemo(() => {
    return materials.filter(mat => {
      const matchSearch = !search ||
        mat.name.toLowerCase().includes(search.toLowerCase()) ||
        mat.category.toLowerCase().includes(search.toLowerCase());
      const matchCat = category === 'all' || mat.category === category;
      const matchType = laserType === 'all' ||
        (mat.compatible_types || []).includes(laserType);
      return matchSearch && matchCat && matchType;
    });
  }, [materials, search, category, laserType]);

  const neverLaser = filtered.filter(m => m.never_laser);
  const safeMats   = filtered.filter(m => !m.never_laser);

  if (loading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.LASER_MATERIALS)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading materials guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      data-testid="laser-materials-guide"
      style={bgStyle(IMAGES.LASER_MATERIALS)}
    >
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <BookOpen className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">MATERIAL REFERENCE</div>
            <h1
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Laser Material Guide
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">
              Power · Speed · Safety information for {materials.filter(m => !m.never_laser).length} laser materials
            </p>
          </div>
        </div>
      </div>

      {/* Safety banner */}
      <div className="bg-red-900/60 border border-red-500/50 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300 mb-1">Material Safety Warning</p>
            <p className="text-sm text-red-200">
              Never laser PVC, ABS, polycarbonate, HDPE, fibreglass, or chrome-tanned leather.
              These release toxic and corrosive fumes. See the <strong>Never Laser</strong> section below.
              Always test unknown materials with a small sample in a ventilated area.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="space-y-3 mb-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search materials..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
          />
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                category === c.id
                  ? c.id === 'never_laser' ? 'bg-red-600 text-white' : 'bg-orange-500 text-black'
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Laser type filter */}
        <div className="flex gap-2">
          {LASER_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => setLaserType(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                laserType === t.id ? 'bg-orange-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* NEVER LASER section — always shown at top when visible */}
      {(category === 'all' || category === 'never_laser') && neverLaser.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-red-500/40" />
            <span className="text-red-400 text-sm font-bold uppercase tracking-wider">⛔ Never Laser — Toxic Materials</span>
            <div className="h-px flex-1 bg-red-500/40" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {neverLaser.map(mat => (
              <div
                key={mat.id}
                className="bg-red-950 border border-red-500/50 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span className="text-red-300 font-bold text-sm">{mat.name}</span>
                    </div>
                  </div>
                  <Badge className="bg-red-700 text-white text-xs">NEVER LASER</Badge>
                </div>
                <p className="text-red-200 text-xs leading-relaxed">{mat.never_reason}</p>
                {mat.safety_notes?.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {mat.safety_notes.map((note, i) => (
                      <p key={i} className="text-red-300/80 text-xs">• {note}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Safe materials */}
      {category !== 'never_laser' && (
        <div className="space-y-3">
          {safeMats.length === 0 && !loading && (
            <div className="text-center py-16 text-zinc-500">
              <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No materials match your filters.</p>
            </div>
          )}

          {safeMats.map(mat => {
            const isOpen = expanded[mat.id];
            return (
              <Card key={mat.id} className="bg-zinc-900 border-zinc-800">
                <CardHeader
                  className="cursor-pointer select-none pb-3"
                  onClick={() => toggle(mat.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <CardTitle className="text-white text-base">{mat.name}</CardTitle>
                        <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-xs capitalize">
                          {mat.category}
                        </Badge>
                        {mat.air_assist_required && (
                          <Badge className="bg-blue-700/60 text-blue-200 text-xs flex items-center gap-1">
                            <Wind className="w-2.5 h-2.5" /> Air assist
                          </Badge>
                        )}
                      </div>
                      {/* Laser type compatibility */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-zinc-500 text-xs">Compatible:</span>
                        {(mat.compatible_types || []).map(t => (
                          <Badge key={t} className={`${TYPE_BADGE[t] || 'bg-zinc-700'} text-white text-xs capitalize`}>
                            {t}
                          </Badge>
                        ))}
                        {(mat.compatible_types || []).length === 0 && (
                          <span className="text-zinc-600 text-xs">None</span>
                        )}
                        <span className="text-zinc-600 text-xs ml-2">
                          {mat.cut && mat.engrave ? 'Cut + Engrave' : mat.cut ? 'Cut only' : mat.engrave ? 'Engrave only' : ''}
                        </span>
                      </div>
                    </div>
                    {isOpen
                      ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0 ml-2" />
                      : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0 ml-2" />}
                  </div>
                </CardHeader>

                {isOpen && (
                  <CardContent className="space-y-5 pt-0">
                    {/* Power / speed settings by wattage */}
                    {mat.power_settings?.length > 0 && (
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-orange-400" />
                          Settings by Laser Wattage
                        </h4>
                        <div className="overflow-x-auto rounded-lg border border-zinc-800">
                          <table className="w-full text-xs">
                            <thead className="bg-zinc-800">
                              <tr>
                                <th className="px-3 py-2 text-left text-zinc-400 font-medium">Laser</th>
                                <th className="px-3 py-2 text-left text-zinc-400 font-medium">Power</th>
                                <th className="px-3 py-2 text-left text-zinc-400 font-medium">Speed</th>
                                <th className="px-3 py-2 text-left text-zinc-400 font-medium">Passes</th>
                                {mat.power_settings.some(s => s.note) && (
                                  <th className="px-3 py-2 text-left text-zinc-400 font-medium">Note</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {mat.power_settings.map((ps, i) => {
                                const isCut = ps.cut_power && ps.cut_power !== 'N/A';
                                return (
                                  <tr key={i} className={i % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-800/40'}>
                                    <td className="px-3 py-2 text-zinc-300 font-medium">{ps.wattage}</td>
                                    <td className="px-3 py-2 text-orange-400 font-mono">
                                      {isCut ? `Cut: ${ps.cut_power}` : ''}{isCut && ps.engrave_power && ps.engrave_power !== 'N/A' ? ' / ' : ''}
                                      {ps.engrave_power && ps.engrave_power !== 'N/A' ? `Engrave: ${ps.engrave_power}` : ''}
                                    </td>
                                    <td className="px-3 py-2 text-zinc-300">
                                      {ps.cut_speed && ps.cut_speed !== 'N/A' ? `Cut: ${ps.cut_speed}` : ''}
                                      {ps.engrave_speed && ps.engrave_speed !== 'N/A' ? (ps.cut_speed && ps.cut_speed !== 'N/A' ? ' / Eng: ' : 'Eng: ') + ps.engrave_speed : ''}
                                    </td>
                                    <td className="px-3 py-2 text-zinc-300">{ps.passes}</td>
                                    {mat.power_settings.some(s => s.note) && (
                                      <td className="px-3 py-2 text-zinc-500 text-xs">{ps.note || ''}</td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-zinc-600 text-xs mt-1">
                          Speeds in mm/min for diode, mm/s for CO2 unless noted. Always test on scrap first.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Safety notes */}
                      {mat.safety_notes?.length > 0 && (
                        <div>
                          <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            Safety Notes
                          </h4>
                          <ul className="space-y-1">
                            {mat.safety_notes.map((note, i) => (
                              <li key={i} className="flex items-start gap-2 text-yellow-300 text-xs">
                                <span className="flex-shrink-0 mt-0.5">•</span>
                                {note}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Pro tips */}
                      {mat.pro_tips?.length > 0 && (
                        <div>
                          <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                            <Info className="w-4 h-4 text-blue-400" />
                            Pro Tips
                          </h4>
                          <ul className="space-y-1">
                            {mat.pro_tips.map((tip, i) => (
                              <li key={i} className="flex items-start gap-2 text-blue-200 text-xs">
                                <span className="flex-shrink-0 mt-0.5">•</span>
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Never Laser standalone view */}
      {category === 'never_laser' && neverLaser.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-30 text-green-500" />
          <p>No matching entries.</p>
        </div>
      )}
    </div>
  );
};

export default MaterialsGuide;
